// frontend/src/components/admin/GlobalConsumptionDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';

const GlobalConsumptionDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchGlobalDashboard();
    const interval = setInterval(fetchGlobalDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchGlobalDashboard = async () => {
    try {
      const response = await api.get('/monitoring/global');
      setData(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return Math.round(amount).toLocaleString('fr-FR');
  };

  if (loading) return <Loading />;

  if (!data) {
    return (
      <div className="p-6">
        <Card>
          <p className="text-center text-gray-500 py-8">
            Aucune donnée disponible
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">
        📊 Dashboard Consommation Global
      </h1>

      {/* Totaux Globaux */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <h3 className="text-lg font-semibold mb-2">Associations actives</h3>
          <p className="text-4xl font-bold">{data.count}</p>
        </Card>

        {/* ✅ CORRIGÉ : totalConsumption.energy = somme des kwh du mois (côté backend) */}
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <h3 className="text-lg font-semibold mb-2">Énergie ce mois</h3>
          <p className="text-4xl font-bold">{data.totalConsumption.energy.toFixed(3)}</p>
          <p className="text-sm mt-1">kWh (cumulé depuis le 1er)</p>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <h3 className="text-lg font-semibold mb-2">Coût total ce mois</h3>
          <p className="text-4xl font-bold">{formatCurrency(data.totalConsumption.cost)}</p>
          <p className="text-sm mt-1">FCFA TTC</p>
        </Card>
      </div>

      {/* Par Owner */}
      <Card title="👥 Consommation par Owner" className="mb-8">
        {data.byOwner.length > 0 ? (
          <div className="space-y-3">
            {data.byOwner.map((owner) => (
              <div
                key={owner.owner.id}
                className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition cursor-pointer"
                onClick={() => navigate(`/owners/${owner.owner.id}/consumption`)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-gray-800">{owner.owner.name}</h4>
                    <p className="text-sm text-gray-600">{owner.count} association(s)</p>
                  </div>
                  <div className="text-right">
                    {/* ✅ CORRIGÉ : energy vient de costs.consumption.kwh (différence du mois) */}
                    <p className="text-lg font-bold text-green-600">
                      {owner.energy.toFixed(3)} kWh
                    </p>
                    <p className="text-xs text-gray-400 mb-1">ce mois</p>
                    <p className="text-sm font-semibold text-orange-600">
                      {formatCurrency(owner.cost)} FCFA TTC
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-4">
            Aucune donnée disponible
          </p>
        )}
      </Card>

      {/* Toutes les Associations */}
      <Card title="🔗 Toutes les Associations">
        {data.associations.length > 0 ? (
          <div className="space-y-3">
            {data.associations.map((item) => (
              <div
                key={item.association.id}
                className="bg-gray-50 rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-bold text-gray-800">{item.association.name}</h4>
                    {item.association.owner && (
                      <p className="text-sm text-gray-600">
                        Owner: {item.association.owner.name}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/associations/${item.association.id}/dashboard`)}
                    className="text-xs"
                  >
                    Voir
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {/* ✅ Puissance temps réel → garder consumption.power */}
                  <div>
                    <p className="text-gray-500">Puissance</p>
                    <p className="font-bold">{item.consumption.power.toFixed(1)} W</p>
                    <p className="text-xs text-gray-400">temps réel</p>
                  </div>

                  {/* ✅ CORRIGÉ : Énergie du mois = costs.consumption.kwh */}
                  <div>
                    <p className="text-gray-500">Énergie ce mois</p>
                    <p className="font-bold text-green-600">
                      {(item.costs.consumption.kwh).toFixed(3)} kWh
                    </p>
                    <p className="text-xs text-gray-400">depuis le 1er</p>
                  </div>

                  {/* ✅ CORRIGÉ : Coût HT = costs.totals.costWithoutTaxes */}
                  <div>
                    <p className="text-gray-500">Coût HT</p>
                    <p className="font-bold">{formatCurrency(item.costs.totals.costWithoutTaxes)} FCFA</p>
                  </div>

                  {/* ✅ Coût TTC */}
                  <div>
                    <p className="text-gray-500">Coût TTC</p>
                    <p className="font-bold text-orange-600">
                      {formatCurrency(item.costs.totals.totalCost)} FCFA
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-4">
            Aucune association active
          </p>
        )}
      </Card>
    </div>
  );
};

export default GlobalConsumptionDashboard;